import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Plus, Trash2, AlertTriangle, History } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["Open", "In progress", "Mitigating", "Closed", "Accepted"];
const CATEGORIES = ["Risk", "Assumption", "Issue", "Dependency"];
const IMPACTS = ["High", "Medium", "Low"];
const RAGS = ["red", "amber", "green"];
const OPEN_STATUSES = ["Open", "In progress", "Mitigating"];

const ragCls = {
  red: "text-red-700 bg-red-50 border-red-200",
  amber: "text-amber-700 bg-amber-50 border-amber-200",
  green: "text-emerald-700 bg-emerald-50 border-emerald-200",
};
const catCls = (c) => c === "Risk" ? "text-red-600" : c === "Issue" ? "text-amber-600" : c === "Dependency" ? "text-violet-600" : "text-zinc-500";
const isOverdue = (i) => i.due_date && OPEN_STATUSES.includes(i.status) && new Date(i.due_date) < new Date(new Date().toDateString());
const inputCls = "border border-zinc-300 rounded px-2 py-1 text-[12px] bg-white";

function RaidRow({ item, onSave, onDelete, busy }) {
  const [showHist, setShowHist] = useState(false);
  const patch = (body) => onSave(item.id, body);
  const overdue = isOverdue(item);
  return (
    <div data-testid={`raid-row-${item.id}`} className={`px-3 py-2.5 ${overdue ? "bg-red-50/40" : ""}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <span className="text-[11px] text-zinc-400 font-mono w-16 pt-1 flex-none">{item.ref}</span>
        <span className={`text-[11px] font-semibold w-20 pt-1 flex-none ${catCls(item.category)}`}>{item.category}</span>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[13px] text-zinc-800 flex items-center gap-1.5 flex-wrap">
            {item.item}
            {item.source === "tom" && (
              <span data-testid={`raid-tom-badge-${item.id}`} title="Promoted from the Delivery TOM recommendation engine"
                className="text-[9px] tracking-[0.08em] uppercase text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">From TOM</span>
            )}
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5">{item.area} · {item.action}</div>
          {overdue && <span className="inline-flex items-center gap-1 text-[10px] text-red-600 font-semibold mt-1"><AlertTriangle size={11} /> Overdue</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <select data-testid={`raid-status-${item.id}`} className={inputCls} value={item.status} disabled={busy}
            onChange={(e) => patch({ status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid={`raid-rag-${item.id}`} className={`${inputCls} font-semibold ${ragCls[item.rag]}`} value={item.rag} disabled={busy}
            onChange={(e) => patch({ rag: e.target.value })}>
            {RAGS.map((r) => <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>)}
          </select>
          <input data-testid={`raid-owner-${item.id}`} className={`${inputCls} w-28`} defaultValue={item.owner} disabled={busy}
            onBlur={(e) => e.target.value !== item.owner && patch({ owner: e.target.value })} title="Owner" />
          <input data-testid={`raid-due-${item.id}`} type="date" className={inputCls} defaultValue={item.due_date || ""} disabled={busy}
            onChange={(e) => patch({ due_date: e.target.value || null })} title="Due date" />
          {(item.updates || []).length > 0 && (
            <button data-testid={`raid-hist-btn-${item.id}`} onClick={() => setShowHist((v) => !v)} title="Update history"
              className="text-zinc-400 hover:text-black p-1"><History size={14} /></button>
          )}
          <button data-testid={`raid-delete-${item.id}`} onClick={() => onDelete(item)} disabled={busy}
            className="text-zinc-300 hover:text-red-600 p-1" title={item.seed_ref ? "Hide (seeded)" : "Delete"}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {showHist && (
        <div data-testid={`raid-history-${item.id}`} className="mt-2 ml-16 border-l-2 border-zinc-200 pl-3 space-y-1">
          {(item.updates || []).map((u, i) => (
            <div key={i} className="text-[11px] text-zinc-500">
              <span className="text-zinc-400">{new Date(u.at).toLocaleDateString("en-GB")}</span>
              {u.from_status && <> · {u.from_status} → <b>{u.status}</b></>}
              {u.note && <> · {u.note}</>}
              {u.by && <span className="text-zinc-300"> · {u.by}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddRaidForm({ onAdd, busy }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "Risk", item: "", area: "Programme", owner: "", impact: "Medium", action: "", due_date: "" });
  const submit = async () => {
    if (!f.item.trim()) { toast.error("Enter an item description"); return; }
    await onAdd(f);
    setF({ category: "Risk", item: "", area: "Programme", owner: "", impact: "Medium", action: "", due_date: "" });
    setOpen(false);
  };
  if (!open) return (
    <button data-testid="raid-add-toggle" onClick={() => setOpen(true)}
      className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5">
      <Plus size={14} /> Add item
    </button>
  );
  return (
    <div data-testid="raid-add-form" className="border border-zinc-200 rounded-lg p-3 bg-zinc-50 space-y-2">
      <div className="grid sm:grid-cols-2 gap-2">
        <select data-testid="raid-add-category" className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="raid-add-impact" className={inputCls} value={f.impact} onChange={(e) => setF({ ...f, impact: e.target.value })}>
          {IMPACTS.map((c) => <option key={c} value={c}>Impact: {c}</option>)}
        </select>
        <input data-testid="raid-add-item" className={`${inputCls} sm:col-span-2`} placeholder="What is the risk / assumption / issue / dependency?" value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} />
        <input data-testid="raid-add-area" className={inputCls} placeholder="Area / value stream" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} />
        <input data-testid="raid-add-owner" className={inputCls} placeholder="Owner" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} />
        <input data-testid="raid-add-action" className={`${inputCls} sm:col-span-2`} placeholder="Mitigation / next action" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} />
        <input data-testid="raid-add-due" type="date" className={inputCls} value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} />
      </div>
      <div className="flex gap-2">
        <button data-testid="raid-add-submit" onClick={submit} disabled={busy}
          className="text-[12px] tracking-[0.12em] uppercase bg-[var(--e360-primary)] text-white px-4 py-1.5 rounded disabled:opacity-40">Add</button>
        <button onClick={() => setOpen(false)} className="text-[12px] tracking-[0.12em] uppercase text-zinc-500 px-3 py-1.5">Cancel</button>
      </div>
    </div>
  );
}

const Stat = ({ label, value, cls = "text-black" }) => (
  <div className="text-center">
    <div className={`text-2xl font-semibold ${cls}`}>{value}</div>
    <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">{label}</div>
  </div>
);

const FilterChip = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`text-[11px] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-[var(--e360-primary)] text-white border-[var(--e360-primary)]" : "text-zinc-500 border-zinc-300 hover:border-zinc-400"}`}>
    {children}
  </button>
);

export default function RaidRegister({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cat, setCat] = useState("All");
  const [showClosed, setShowClosed] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [tomOnly, setTomOnly] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/raid`);
      setData(data);
    } catch { toast.error("Could not load the RAID register"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const withBusy = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setData(data); if (ok) toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const sync = () => withBusy(() => api.post(`/admin/enterprise360/clients/${clientId}/raid/seed`), "Synced from blueprint");
  const add = (body) => withBusy(() => api.post(`/admin/enterprise360/clients/${clientId}/raid`, body), "Item added");
  const save = (itemId, body) => withBusy(() => api.put(`/admin/enterprise360/clients/${clientId}/raid/${itemId}`, body));
  const del = (item) => {
    if (!window.confirm(item.seed_ref ? "Hide this seeded item from the register?" : "Delete this item?")) return;
    withBusy(() => api.delete(`/admin/enterprise360/clients/${clientId}/raid/${item.id}`), "Removed");
  };

  const s = data?.summary || {};
  const allItems = data?.items || [];
  const items = useMemo(() => allItems.filter((i) =>
    (cat === "All" || i.category === cat)
    && (showClosed || OPEN_STATUSES.includes(i.status))
    && (!overdueOnly || isOverdue(i))
    && (!tomOnly || i.source === "tom")
  ), [allItems, cat, showClosed, overdueOnly, tomOnly]);
  const tomCount = useMemo(() => allItems.filter((i) => i.source === "tom").length, [allItems]);
  const overallCls = s.overall === "red" ? ragCls.red : s.overall === "amber" ? ragCls.amber : ragCls.green;

  return (
    <div data-testid="raid-register">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-[12px] text-zinc-500 max-w-xl">Risks, assumptions, issues &amp; dependencies tracked against status, owner and due-date — the living register, not a static artefact.</p>
        <div className="flex items-center gap-2">
          {data?.seed_available > 0 && (
            <span data-testid="raid-seed-available" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
              {data.seed_available} new from blueprint
            </span>
          )}
          <button data-testid="raid-sync-btn" onClick={sync} disabled={busy}
            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5 disabled:opacity-40">
            <RefreshCw size={13} /> Sync from blueprint
          </button>
        </div>
      </div>

      <div data-testid="raid-summary" className="flex items-center gap-5 flex-wrap border border-zinc-200 rounded-lg px-4 py-3 mb-4">
        <span data-testid="raid-overall" className={`text-[12px] rounded-full px-3 py-1 border font-semibold ${overallCls}`}>
          ● Overall {s.overall === "green" ? "on track" : s.overall === "amber" ? "watch" : "at risk"}
        </span>
        <Stat label="Open" value={s.open ?? 0} />
        <Stat label="Overdue" value={s.overdue ?? 0} cls={s.overdue ? "text-red-600" : "text-black"} />
        <Stat label="Red" value={s.reds ?? 0} cls="text-red-600" />
        <Stat label="Amber" value={s.ambers ?? 0} cls="text-amber-600" />
        <Stat label="Closed" value={s.closed ?? 0} cls="text-zinc-400" />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="raid-filters">
        <FilterChip testId="raid-filter-all" active={cat === "All"} onClick={() => setCat("All")}>All ({allItems.length})</FilterChip>
        {CATEGORIES.map((c) => (
          <FilterChip key={c} testId={`raid-filter-${c.toLowerCase()}`} active={cat === c} onClick={() => setCat(c)}>
            {c} ({s.by_category?.[c] ?? 0})
          </FilterChip>
        ))}
        <span className="w-px h-5 bg-zinc-200 mx-1" />
        <FilterChip testId="raid-filter-overdue" active={overdueOnly} onClick={() => setOverdueOnly((v) => !v)}>Overdue only</FilterChip>
        <FilterChip testId="raid-filter-closed" active={showClosed} onClick={() => setShowClosed((v) => !v)}>Show closed</FilterChip>
        {tomCount > 0 && (
          <FilterChip testId="raid-filter-tom" active={tomOnly} onClick={() => setTomOnly((v) => !v)}>From TOM ({tomCount})</FilterChip>
        )}
      </div>

      <div className="mb-3"><AddRaidForm onAdd={add} busy={busy} /></div>

      {loading ? (
        <div className="text-sm text-zinc-400 py-6 text-center">Loading register…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center">{allItems.length === 0 ? "No RAID items yet. Sync from the blueprint or add one manually." : "No items match the current filter."}</div>
      ) : (
        <div className="border border-zinc-200 rounded-lg divide-y" data-testid="raid-list">
          {items.map((it) => <RaidRow key={it.id} item={it} onSave={save} onDelete={del} busy={busy} />)}
        </div>
      )}
    </div>
  );
}
